import Fuse from 'fuse.js';

export interface FuzzyCandidate {
  label: string;
  value: string;
}

/** Small wrapper around fuse.js so the wizard doesn't have to rebuild an index per lookup. */
export class FuzzyIndex {
  private fuse: Fuse<FuzzyCandidate>;

  constructor(private candidates: FuzzyCandidate[]) {
    this.fuse = new Fuse(candidates, { keys: ['label', 'value'], threshold: 0.4, includeScore: true });
  }

  exactMatch(query: string): FuzzyCandidate | null {
    const q = (query || '').trim().toLowerCase();
    if (!q) return null;
    return this.candidates.find(c => c.label.toLowerCase() === q || c.value.toLowerCase() === q) || null;
  }

  bestMatches(query: string, limit = 3): FuzzyCandidate[] {
    if (!query || !this.candidates.length) return [];
    return this.fuse.search(query, { limit }).map(r => r.item);
  }

  /** Same ranking as bestMatches(), but keeps fuse.js's own match score (0 = perfect, up to the
   *  `threshold: 0.4` configured above — anything weaker never comes back at all). Lets a caller
   *  tell "confident enough to auto-pick" apart from "plausible but should ask the user" instead
   *  of treating everything under 0.4 as equally good. */
  bestMatchesWithScore(query: string, limit = 3): Array<FuzzyCandidate & { score: number }> {
    if (!query || !this.candidates.length) return [];
    return this.fuse.search(query, { limit }).map(r => ({ ...r.item, score: r.score ?? 1 }));
  }
}
